/**
 * Cascading (Masonry) grid layout
 *
 * @requires https://github.com/Vestride/Shuffle
 */

import vendorMissing from './vendor-missing'

export default (() => {
  const grid = document.querySelectorAll('.masonry-grid')

  // querySelectorAll never returns null - this used to be `grid === null`,
  // which never fired, so a page without a masonry grid still reached the
  // library calls below only by luck of the empty loop
  if (grid.length === 0) return

  if (vendorMissing('masonry grid', { Shuffle: typeof Shuffle })) return

  for (let i = 0; i < grid.length; i++) {
    /* eslint-disable no-undef */
    const masonry = new Shuffle(grid[i], {
      itemSelector: '.masonry-grid-item',
      sizer: '.masonry-grid-item',
    })
    /* eslint-enable no-undef */
    // Kept on the element for card-expand.js, which lays the grid out again
    // when a card opens (the way gallery.js keeps grid._lgInstance)
    grid[i]._shuffle = masonry

    // Lay out again as images arrive. Not imagesLoaded: for every image that
    // is not complete yet it starts its own download (new Image()), which
    // ignores loading="lazy" and fetches the whole grid on page load. A load
    // listener waits for the browser's own lazy fetch instead; one layout
    // per frame however many images land in it.
    let frame = 0
    const relayout = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        masonry.layout()
      })
    }
    grid[i].querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', relayout, { once: true })
    })

    // Filtering: the group filter (buttons OR select) and the quick search
    // combine - an item stays visible only when it matches the active group
    // AND the typed keyword (the cloud19 isotope semantics).
    const filtersWrap = grid[i].closest('.masonry-filterable')
    if (filtersWrap === null) continue
    const filters = filtersWrap.querySelectorAll(
      '.masonry-filters [data-group]'
    )
    const select = filtersWrap.querySelector('.masonry-filters-select')
    const quicksearch = filtersWrap.querySelector('.masonry-quicksearch')

    const isGallery = grid[i].classList.contains('gallery')

    let activeGroup = 'all'
    let keyword = ''

    const applyFilters = () => {
      masonry.filter((element) => {
        let groups = []
        try {
          groups = JSON.parse(element.getAttribute('data-groups') || '[]')
        } catch (e) {
          groups = []
        }
        const groupResult =
          activeGroup === 'all' || groups.indexOf(activeGroup) !== -1
        const searchResult =
          keyword === '' ||
          element.textContent.toLowerCase().indexOf(keyword) !== -1
        return groupResult && searchResult
      })

      // Refresh lightgallery with only visible items after shuffle transition
      if (isGallery) {
        setTimeout(() => {
          // gallery.js creates the instance on the first click only, so the
          // selector is kept on the grid for it as well
          grid[i]._lgSelector =
            activeGroup === 'all' && keyword === ''
              ? '.gallery-item'
              : '.masonry-grid-item:not(.shuffle-item--hidden) .gallery-item'
          const lgInst = grid[i]._lgInstance
          if (lgInst) {
            lgInst.settings.selector = grid[i]._lgSelector
            lgInst.refresh()
          }
        }, 300)
      }
    }

    for (let n = 0; n < filters.length; n++) {
      filters[n].addEventListener('click', function (e) {
        const current = filtersWrap.querySelector('.masonry-filters .active')
        if (current !== null) {
          current.classList.remove('active')
        }
        this.classList.add('active')
        activeGroup = this.dataset.group
        applyFilters()
        e.preventDefault()
      })
    }

    if (select !== null) {
      select.addEventListener('change', function () {
        activeGroup = this.value
        applyFilters()
      })
    }

    if (quicksearch !== null) {
      let debounce
      quicksearch.addEventListener('input', function () {
        clearTimeout(debounce)
        debounce = setTimeout(() => {
          keyword = quicksearch.value.trim().toLowerCase()
          applyFilters()
        }, 200)
      })
    }
  }
})()
